import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {PropsWithChildren, useRef, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {mobileOnlyLinaria} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {useIsomorphicLayoutEffect} from '../../../hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';
import {PriceV2} from '../../../types/Price';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {BadgeListSmall} from '../BadgeList/BadgeList';
import {FinalPrice, FinalPriceVatWrapper} from '../FinalPrice/FinalPrice';
import {SizeOptionProps} from './types';

const FONT_CONFIG = {
  LABEL_SIZE: 14,
  LABEL_WEIGHT: 600,
  SUBLABEL_SIZE: 10,
  SUBLABEL_WEIGHT: 600,
  BADGE_SIZE: 10,
  BADGE_WEIGHT: 600,
  NOTIFY_ME_SIZE: 12,
  NOTIFY_ME_WEIGHT: 700,
  PRICE_SIZE: 12,
  PRICE_WEIGHT: 400,
} as const;

const LAYOUT_CONFIG = {
  WRAPPER_PADDING: 40,
  LABEL_MARGIN: 10,
  CONTENT_GAP: 18,
  END_CONTENT_GAP: 8,
  BADGE_BASE_WIDTH: 40,
  BADGE_LABEL_PADDING: 16,
  ICON_WIDTH: 16,
  FIXED_BADGE_WIDTH: 30,
  PRICE_PADDING: 10,
} as const;

const estimateTextWidth = (
  text: string,
  fontSize: number,
  fontWeight: number,
): number => {
  const weightFactor = fontWeight >= 700 ? 0.6 : 0.55;
  return text.length * fontSize * weightFactor;
};

const estimateBadgeWidth = (
  badges: Array<{kind: string; label?: string}> | undefined,
): number => {
  if (!badges?.length) return 0;
  let totalWidth = badges.length * LAYOUT_CONFIG.BADGE_BASE_WIDTH;
  badges.forEach(badge => {
    if ('label' in badge && badge.label) {
      totalWidth +=
        estimateTextWidth(
          badge.label,
          FONT_CONFIG.BADGE_SIZE,
          FONT_CONFIG.BADGE_WEIGHT,
        ) + LAYOUT_CONFIG.BADGE_LABEL_PADDING;
    } else if (badge.kind === 'YOUR_SIZE' || badge.kind === 'IN_BASKET') {
      totalWidth += LAYOUT_CONFIG.FIXED_BADGE_WIDTH;
    }
  });
  return totalWidth;
};

export type OptionContentProps = {
  enableEndBadgeBellIcon?: boolean;
  showVatLabelInColumn?: boolean;
};

export const OptionContent: React.FC<
  SizeOptionProps & OptionContentProps & PropsWithChildren
> = ({children, ...props}) => {
  const [hasEnoughSpace, setHasEnoughSpace] = useState(true);
  const {t} = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (!wrapperRef.current) {
      return;
    }

    const wrapperWidth = wrapperRef.current.offsetWidth;

    let labelWidth = props.label
      ? estimateTextWidth(
          props.label,
          FONT_CONFIG.LABEL_SIZE,
          FONT_CONFIG.LABEL_WEIGHT,
        )
      : 0;

    if (props.subLabel) {
      const subLabelWidth = estimateTextWidth(
        props.subLabel,
        FONT_CONFIG.SUBLABEL_SIZE,
        FONT_CONFIG.SUBLABEL_WEIGHT,
      );
      labelWidth = Math.max(labelWidth, subLabelWidth);
    }

    const badgeWidth = estimateBadgeWidth(props.badgesListPositionStart);
    const endBadgeWidth = estimateBadgeWidth(props.badgesListPositionEnd);

    let endContentWidth = 0;
    if (props.soldOut) {
      const notifyMeText = t('Notify me');
      const notifyMeWidth = estimateTextWidth(
        notifyMeText,
        FONT_CONFIG.NOTIFY_ME_SIZE,
        FONT_CONFIG.NOTIFY_ME_WEIGHT,
      );
      endContentWidth =
        notifyMeWidth +
        LAYOUT_CONFIG.ICON_WIDTH +
        LAYOUT_CONFIG.END_CONTENT_GAP;

      if (props.badgesListPositionStart?.length) {
        const soldOutBadgeWidth = estimateBadgeWidth(
          props.badgesListPositionStart,
        );
        endContentWidth += soldOutBadgeWidth + LAYOUT_CONFIG.END_CONTENT_GAP;
      }
    } else {
      let endPriceWidth = 0;
      if (props.displayVariantPrices && props.price) {
        const priceText =
          'finalPrice' in props.price
            ? props.price.finalPrice.priceLabel.text
            : '€XX.XX';
        endPriceWidth =
          estimateTextWidth(
            priceText,
            FONT_CONFIG.PRICE_SIZE,
            FONT_CONFIG.PRICE_WEIGHT,
          ) /
            2 +
          LAYOUT_CONFIG.PRICE_PADDING;
      }

      endContentWidth =
        endBadgeWidth +
        endPriceWidth +
        (endBadgeWidth && endPriceWidth ? LAYOUT_CONFIG.END_CONTENT_GAP : 0);
    }

    const availableWidthForBadge =
      wrapperWidth -
      LAYOUT_CONFIG.WRAPPER_PADDING -
      labelWidth -
      LAYOUT_CONFIG.LABEL_MARGIN -
      endContentWidth -
      LAYOUT_CONFIG.CONTENT_GAP;

    setHasEnoughSpace(
      badgeWidth <= availableWidthForBadge || wrapperWidth < 200,
    );
  }, [
    props.label,
    props.subLabel,
    props.price,
    props.badgesListPositionStart,
    props.badgesListPositionEnd,
    props.displayVariantPrices,
    props.soldOut,
    t,
  ]);

  const hasBadge = !!props.badgesListPositionStart?.length;
  const hasSoldOut = !!props.soldOut;
  const shouldWrapBadge = !hasEnoughSpace && hasBadge && hasSoldOut;
  const showYourSizeBadgeInStart = hasEnoughSpace && hasBadge;

  return (
    <OptionWrapper
      className={props.className}
      data-short-label={hasEnoughSpace}
      ref={wrapperRef}
    >
      <ContentWrapper>
        {children}
        <ContentStart
          {...props}
          showYourSizeBadgeInStart={showYourSizeBadgeInStart}
        />
        <ContentEnd
          {...props}
          shouldWrapBadge={shouldWrapBadge}
          showYourSizeBadgeInStart={showYourSizeBadgeInStart}
        />
      </ContentWrapper>

      {shouldWrapBadge && props.badgesListPositionStart && (
        <OptionBadgeWrapper>
          <BadgeWrapper data-should-wrap={shouldWrapBadge}>
            <BadgeListSmall
              data-should-wrap={shouldWrapBadge}
              entries={props.badgesListPositionStart}
              disableRoundedCorners={false}
              showYourSizeInfoIcon={props.showYourSizeInfoIcon}
            />
          </BadgeWrapper>
        </OptionBadgeWrapper>
      )}
    </OptionWrapper>
  );
};

const ContentStart: React.FC<Partial<SizeOptionProps>> = ({
  label,
  subLabel,
  badgesListPositionBottom,
  badgesListPositionStart,
  soldOut,
  selected,
  dataTestId = 'optionContent',
  showYourSizeBadgeInStart,
  showYourSizeInfoIcon,
}) => {
  return (
    <ContentStartWrapper>
      <LabelWrapper>
        <Label
          data-testid={`${dataTestId}Label`}
          soldOut={soldOut}
          selected={selected}
        >
          {label}
        </Label>
        {subLabel && <SubLabel>{subLabel}</SubLabel>}
        {badgesListPositionBottom && (
          <BadgeListSmall
            entries={badgesListPositionBottom}
            disableRoundedCorners={false}
          />
        )}
      </LabelWrapper>
      {badgesListPositionStart && showYourSizeBadgeInStart && (
        <BadgeWrapper>
          <BadgeListSmall
            entries={badgesListPositionStart}
            disableRoundedCorners={false}
            showYourSizeInfoIcon={showYourSizeInfoIcon}
          />
        </BadgeWrapper>
      )}
    </ContentStartWrapper>
  );
};

const ContentEnd: React.FC<
  Partial<
    SizeOptionProps &
      OptionContentProps & {
        shouldWrapBadge?: boolean;
      }
  >
> = ({
  badgesListPositionEnd,
  soldOut,
  displayVariantPrices,
  enableEndBadgeBellIcon,
  showYourSizeInfoIcon,
  shouldWrapBadge,
  badgesListPositionStart,
  showYourSizeBadgeInStart,
  price,
}) => {
  const {t} = useTranslation();

  const showLocalVatLabel = Boolean(
    price && 'finalPrice' in price && price.finalPrice.vatLabel,
  );

  if (soldOut) {
    return (
      <SoldOutLabelWrapper data-invert-elements={enableEndBadgeBellIcon}>
        <SoldOutLabel data-testid="sizeOptionSoldOutRemindMeLabel">
          {t('Notify me')}
        </SoldOutLabel>
        <Icon
          data-testid="sizeOptionSoldOutRemindMeIcon"
          asset={GenericIconName.Bell}
          size="XS"
          color={COLORS.Grey1}
        />
        {(badgesListPositionStart?.length || 0) > 0 &&
          !shouldWrapBadge &&
          !showYourSizeBadgeInStart && (
            <BadgeWrapper data-should-wrap={shouldWrapBadge}>
              <BadgeListSmall
                data-should-wrap={shouldWrapBadge}
                entries={badgesListPositionStart!}
                disableRoundedCorners={false}
                showYourSizeInfoIcon={showYourSizeInfoIcon}
              />
            </BadgeWrapper>
          )}
      </SoldOutLabelWrapper>
    );
  }

  return (
    <ContentEndWrapper data-show-in-column={showLocalVatLabel}>
      {badgesListPositionEnd && (
        <BadgeListSmall
          entries={badgesListPositionEnd}
          disableRoundedCorners={false}
        />
      )}
      {displayVariantPrices && price && (
        <StyledFinalPrice
          data-testid="finalPrice"
          price={price}
          hideVatLabel={showLocalVatLabel}
        />
      )}
      {badgesListPositionStart &&
        !shouldWrapBadge &&
        !showYourSizeBadgeInStart && (
          <BadgeWrapper data-should-wrap={shouldWrapBadge}>
            <BadgeListSmall
              data-should-wrap={shouldWrapBadge}
              entries={badgesListPositionStart}
              disableRoundedCorners={false}
              showYourSizeInfoIcon={showYourSizeInfoIcon}
            />
          </BadgeWrapper>
        )}

      {showLocalVatLabel && displayVariantPrices && (
        <FinalPriceVatWrapper
          dangerouslySetInnerHTML={{
            __html: (price as PriceV2).finalPrice.vatLabel?.text ?? '',
          }}
        />
      )}
    </ContentEndWrapper>
  );
};

const ContentStartWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-items: center;
  width: 100%;

  &[data-wrap-badge='true'] {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ContentEndWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;

  &[data-show-in-column='true'] {
    flex-direction: column;
    align-items: flex-end;
    gap: 0;
  }
`;

const SoldOutLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: 4px;
  min-width: 111px;

  &[data-invert-elements='true'] {
    flex-direction: row-reverse;
  }
`;

const BadgeWrapper = styled.div`
  &[data-should-wrap='true'] {
    div {
      padding: 8px;
    }
  }
`;

const LabelWrapper = styled.div`
  margin: 0 10px 0 0;
  text-align: left;
`;

const OptionBadgeWrapper = styled.div`
  display: flex;
  padding-left: 26px;
`;

const Label = styled('div')<{soldOut?: boolean; selected?: boolean}>`
  color: ${props =>
    props.soldOut ? COLORS['Text.Grey.Inactive'] : COLORS.Black};
  font-size: ${FONT_SIZES.px14};
  direction: initial;
  white-space: nowrap;
  font-weight: ${props =>
    props.selected ? `${FONT_WEIGHTS.heavy}` : `${FONT_WEIGHTS.mediumBold}`};

  ${mobileOnlyLinaria(`
    font-weight: ${FONT_WEIGHTS.mediumBold};
  `)}

  text-decoration: ${props => (props.soldOut ? 'line-through' : 'none')};
`;

const SubLabel = styled.div`
  margin: 3px 0 5px;
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.Grey1};
`;

const SoldOutLabel = styled.div`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.bold};
  color: ${COLORS.Grey1};

  ${mobileOnlyLinaria(`
    font-weight: ${FONT_WEIGHTS.mediumBold};
  `)}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 18px;
`;

const OptionWrapper = styled.div`
  box-sizing: border-box;
  min-height: 44px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 10px 20px;
  flex-direction: column;
`;

const StyledFinalPrice = styled(FinalPrice)`
  div {
    font-size: ${FONT_SIZES.px12};
  }
`;
